import { useIntl } from 'react-intl'
import { useConfiguration } from 'contexts/configuration'
import { useCategories } from 'pages/documents/utilities'
import { getContentWithoutInterpolatedProperties, getContentWithoutFalsyValues } from 'components/tiptap/extensions/variable/utilities'
import { saveAs } from 'file-saver'
import { get } from 'api'
import { pick, omit, size, reduce, compact as compactObject } from 'utilities/object'

export const modes = ['build', 'fill']

export const implicitSources = [
    {
        type: 'organization',
        name: 'organization',
        label: {
            id: 'noun_organization',
            defaultMessage: 'Organization'
        }
    },
    {
        type: 'user',
        name: 'responsible',
        label: {
            id: 'noun_user_loggedin',
            defaultMessage: 'Logged-in user'
        }
    },
    {
        type: 'universal',
        name: 'universal',
        label: {
            id: 'noun_universal',
            defaultMessage: 'Universal'
        }
    }
].map(s => ({ ...s, implicit: true }))

export const universalValuesKey = 'universal.universal'
export const organizationValueKey = 'organization.organization'
export const userResponsibleValueKey = 'user.responsible'

export const getValueFromSource = ({ type, name }) => ({
    value: null,
    source: {
        type,
        name
    }
})

export const useGetUniversalValue = () => {
    const { formatMessage } = useIntl()

    return value => ({
        value: {
            ...value,
            id: 'universal',
            name: formatMessage({
                id: 'noun_universal',
                defaultMessage: 'Universal'
            })
        },
        source: {
            type: 'universal',
            name: 'universal'
        }
    })
}

export const getOrganizationValue = ({ organization, account }) => {
    const value = !!organization ? {
        ...pick(organization, 'id', 'name'),
        ...(!!account ? {
            account: pick(account, 'company', 'address', 'organizationNumber', 'vatNumber')
        } : null)
    } : null

    return {
        value,
        source: {
            type: 'organization',
            name: 'organization'
        }
    }
}

export const getUserResponsibleValue = ({ me }) => {
    const value = transformUserValue(me ?? null)

    return {
        value,
        source: {
            type: 'user',
            name: 'responsible'
        }
    }
}

export const transformUserValue = user => {
    if(!user) {
        return null
    }

    let { custom, ...rest } = user

    if(!size(custom ?? {})) {
        return rest
    }

    custom = reduce(custom, (accumulator, value, key) => ({
        ...accumulator,
        [`custom.${key}`]: value
    }))

    return {
        ...rest,
        ...custom
    }
}

export const useExportSmartEntity = () => {
    const { formatMessage } = useIntl()
    const { categories } = useCategories()
    const { configuration } = useConfiguration()

    return async (entity, type = 'template') => {
        if(!entity?.id) {
            return
        }

        if(!entity.content) {
            const { ok, response } = await get({
                path: (type === 'template') ?
                    `/documents/templates/${entity.id}` :
                    `/documents/${entity.id}`
            })

            if(!ok) {
                return
            }

            entity = response
        }

        entity = omit(entity,
            'type', 'id', 'versionHash', 'owners', 'shares', 'templateId', 'sourceTypes',
            'uploadedAt', 'uploadedBy', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'
        )

        if(!entity.category) {
            entity.category = categories[0].value
        }

        if(!!entity.sources) {
            if(!entity.sources.length) {
                delete entity.sources
            } else {
                entity.sources = entity.sources
                    .filter(({ type, implicit }) => configuration.enums.smartDocumentSourceType.includes(type) && !implicit)
                    .map(({ id, ...source }) => source) // eslint-disable-line no-unused-vars
            }
        }

        entity.content = getContentWithoutInterpolatedProperties('id', 'value', 'cursor')({ content: entity.content }).content
        entity.content = getContentWithoutFalsyValues({ content: entity.content }).content

        const templateBlob = new Blob(
            [JSON.stringify(compactObject(entity), null, 4)],
            { type: 'application/json' }
        )

        const templateTranslation = formatMessage({
            id: 'documents_smart_template_noun',
            defaultMessage: 'Smart template'
        }).toLowerCase()

        saveAs(templateBlob, `${entity.name} (${templateTranslation}).json`)
    }
}

export const emptity = {
    content: [{
        type: 'section',
        content: []
    }],
    sources: implicitSources
}